<template>
  <div class="dt-page-length">
    <button
      ref="popoverTrigger"
      type="button"
      class="flex gap-2 items-center px-3 pt-2 text-sm text-center text-gray-900 bg-white rounded-lg hover:bg-gray-100 focus:ring-4 focus:outline-none dark:bg-gray-600 dark:hover:bg-gray-700"
    >
      {{ selectedOption?.label || "--" }}
      <ChevronDown height="12" />
    </button>
    <div
      ref="popoverContent"
      data-popover
      role="tooltip"
      class="flex flex-col z-10 invisible justify-end py-1 mb-4 space-y-2 bg-white border border-gray-100 rounded-lg shadow-sm dark:border-gray-600 dark:bg-gray-700"
    >
      <ul class="p-3">
        <li
          v-for="option in options"
          :key="option.value"
          class="text-gray-700 text-left px-2 rounded-lg font-medium block py-2 no-underline hover:bg-gray-100 cursor-pointer"
          @click="handleClick(option)"
        >
          <span>{{ option.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PopoverInterface, PopoverOptions } from "flowbite";
import type { InstanceOptions } from "flowbite";
import { Popover } from "flowbite";
import { computed, onMounted, ref } from "vue";
import { generateUID } from "@/lib/helpers";

import ChevronDown from "@/images/chevron-down.svg?component";

const popover = ref<PopoverInterface | null>(null);
const popoverTrigger = ref<HTMLElement | null>(null);
const popoverContent = ref<HTMLElement | null>(null);

// options with default values
const popoverOptions: PopoverOptions = {
  placement: "top",
  triggerType: "click",
  offset: -10
};
const instanceOptions: InstanceOptions = {
  id: `datatablePageLength-${generateUID()}`,
  override: true
};
interface Option {
  label: string;
  value: string;
}
const props = defineProps<{ options: Option[]; modelValue: string }>();
const emit = defineEmits(["update:modelValue"]);
function handleClick(option: Option) {
  emit("update:modelValue", option.value);
}
const selectedOption = computed(() =>
  props.options.find(
    (opt) => opt.value.toString() === props.modelValue?.toString()
  )
);
onMounted(() => {
  if (popoverTrigger.value && popoverContent.value) {
    popover.value = new Popover(
      popoverContent.value,
      popoverTrigger.value,
      popoverOptions,
      instanceOptions
    );
  }
});
</script>

<style>
.dt-page-length {
  display: block;
  /* Where several of these end up as siblings, only the last one shows.
     Expressed as "has a later sibling of the same kind" rather than
     :last-of-type, which is also false whenever ANY later sibling shares the
     tag name: PaginationControls' jump-to-page div is a later <div>, so
     rendering it hid this control outright - and since shrinking the page size
     is how you drop back under the jump threshold, there was no way to get it
     back. */
  &:has(~ .dt-page-length) {
    display: none;
  }
}
</style>
